import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { ChartConfiguration, TooltipItem } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  AuxCardSelectComponent,
  AuxSelectConfig,
} from '@shared/components/aux-card-select/aux-card-select.component';
import { VendorDropdownComponent } from '@shared/components/vendor-dropdown/vendor-dropdown.component';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { Utils } from '@shared/utils/utils';

import { PaymentsHistoryComponent } from './components/payments-history/payments-history.component';
import { PaymentScheduleQuery } from './state/payment-schedule.query';
import { PaymentScheduleService } from './state/payment-schedule.service';
import { PaymentQuarterData } from './state/payment-schedule.store';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NoopValueAccessorDirective,
    VendorDropdownComponent,
    FormsModule,
    ReactiveFormsModule,
    AuxCardSelectComponent,
    NgChartsModule,
    PaymentsHistoryComponent,
    MoneyPipe,
    AsyncPipe,
    DatePipe,
  ],
  selector: 'aux-payment-schedule',
  styles: [],
  templateUrl: './payment-schedule.component.html',
})
export class PaymentScheduleComponent {
  organizationQuery = inject(OrganizationQuery);

  private mainQuery = inject(MainQuery);
  private organizationService = inject(OrganizationService);
  private organizationStore = inject(OrganizationStore);
  private paymentScheduleQuery = inject(PaymentScheduleQuery);
  private paymentScheduleService = inject(PaymentScheduleService);

  chart$: Observable<ChartConfiguration<'bar'>> = this.paymentScheduleQuery
    .select('PAYMENT_SCHEDULE_CUMULATIVE_PAYMENTS_VS_CUMULATIVE_WP')
    .pipe(
      map((data) => {
        const quarters = Object.keys(data);

        return <ChartConfiguration<'bar'>>{
          data: {
            datasets: this.getDatasets(data),
            labels: quarters,
          },
          options: {
            maintainAspectRatio: false,
            plugins: {
              tooltip: {
                callbacks: {
                  label(tooltipItem: TooltipItem<'bar'>): string | string[] | void {
                    if (tooltipItem.raw && typeof tooltipItem.raw === 'number') {
                      return Utils.currencyFormatter(tooltipItem.raw, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      });
                    }

                    return `${tooltipItem.raw || ''}`;
                  },
                },
              },
            },
            scales: {
              x: {
                ticks: {
                  autoSkip: false,
                },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  callback: (value) => {
                    return Utils.currencyFormatter(value as number, {
                      currency: 'USD',
                      maximumFractionDigits: 1,
                      minimumFractionDigits: 0,
                      notation: 'compact',
                      style: 'currency',
                    });
                  },
                },
              },
            },
          },
          type: 'bar',
        };
      })
    );
  filter$: Observable<AuxSelectConfig> = this.paymentScheduleQuery
    .select('PAYMENT_SCHEDULE_CASH_REQUIREMENTS')
    .pipe(
      map((arr) => {
        return {
          data: arr.map(({ pm_total }) => Utils.currencyFormatter(pm_total)),
          header: 'Cash Requirements',
          options: arr.map(({ expense_months_back }) => expense_months_back),
        } as AuxSelectConfig;
      })
    );
  legends = [
    {
      color: '#BACAD0',
      header: 'Milestone Payments',
    },
    {
      color: '#f3f6f7',
      header: 'Unit-Based Payments',
    },
    {
      color: '#094673',
      header: 'WP',
    },
  ];
  loading$ = this.paymentScheduleQuery.selectLoading();
  middle$ = this.paymentScheduleQuery.select('PAYMENT_SCHEDULE_PREPAID_BALANCE_IN_ESCROW');
  right$ = this.paymentScheduleQuery.select('PAYMENT_SCHEDULE_NEXT_PAYMENT_MILESTONE');
  selectedVendor = new UntypedFormControl('');
  table$ = this.paymentScheduleQuery.select('PAYMENT_SCHEDULE_MILESTONES');

  constructor() {
    this.mainQuery
      .select('trialKey')
      .pipe(
        switchMap(() => {
          return this.paymentScheduleService.getData();
        })
      )
      .pipe(takeUntilDestroyed())
      .subscribe();

    this.organizationService
      .get()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const org = this.organizationQuery.getActive();

        if (org) {
          this.selectedVendor.setValue(org.id);
        }
      });
  }

  getDatasets(data: Record<string, PaymentQuarterData>) {
    return [
      {
        backgroundColor: '#BACAD0',
        borderColor: '#BACAD0',
        data: Object.values(data).map((s) => s.payment),
        label: 'Payments',
        maxBarThickness: 40,
        stack: 'stack 0',
      },
      {
        backgroundColor: '#f3f6f7',
        borderColor: '#f3f6f7',
        data: [],
        label: 'Investigator Pre-payment (Forecast)',
        maxBarThickness: 40,
        stack: 'stack 0',
      },
      {
        backgroundColor: '#094673',
        borderColor: '#094673',
        data: Object.values(data).map((s) => s.wp),
        label: 'WP',
        maxBarThickness: 40,
        stack: 'stack 1',
      },
    ];
  }

  onOrganizationSelected(orgId: string) {
    this.organizationStore.setActive(orgId);
  }
}
